import React from 'react';
import styled, { StyledComponentClass } from 'styled-components';
import Ringside from 'ringside';

import { colors } from '../../styles';

export interface Props {
  activator?: Element;
  children: any;
  className?: string;
  clickable?: boolean;
}

export interface State {
  orientation: string;
  alignment: string;
  left: string;
  top: string;
}

class GraphToolTipContent extends React.Component<Props, State> {
  public el;

  public state: State = {
    orientation: 'top',
    alignment: 'start',
    left: '0px',
    top: '0px',
  };

  public componentDidMount() {
    window.addEventListener('scroll', this.setTooltipPosition, false);
    this.setTooltipPosition();
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.setTooltipPosition, false);
  }

  public render() {
    const { children, className } = this.props;
    const { orientation, alignment, left, top } = this.state;
    const isText: boolean = typeof children === 'string';

    return (
      <div
        className={`${className} ${orientation} ${alignment} ${
          isText ? 'text' : ''
        }`}
        ref={el => {
          if (el) {
            this.el = el;
            this.el.style.left = left;
            this.el.style.top = top;
          }
        }}
        style={{ top, left }}
      >
        {children}
      </div>
    );
  }

  private setTooltipPosition = () => {
    const { orientation, alignment, left, top } = this.positionToolTip();
    this.setState({ orientation, alignment, left, top });
  };

  private positionToolTip = (): State => {
    const OUTER_SPACING = 10;
    const activator = this.props.activator.getBoundingClientRect();
    const tooltip = this.el.getBoundingClientRect();
    const viewPort = this.getViewPort(document.body);
    const ringside = new Ringside(
      activator,
      viewPort,
      tooltip.height + OUTER_SPACING,
      tooltip.width + OUTER_SPACING,
    );

    let position;
    const orientations = ['right', 'left', 'top', 'bottom'];
    const alignments = ['start', 'end'];

    for (const orientation of orientations) {
      for (const alignment of alignments) {
        const positioning = ringside[orientation]()[alignment].bottom;
        if (positioning.fits) {
          position = {
            alignment,
            orientation,
            left: positioning.x,
            top: positioning.y,
          };
          break;
        }
      }
    }

    if (this.alignedRight(position)) {
      position.left += OUTER_SPACING;
    }

    if (this.alignedBottom(position)) {
      position.top += OUTER_SPACING;
    }
    return position;
  };

  private alignedRight(p) {
    return (
      p.orientation === 'right' ||
      (p.orientation === 'top' && p.alignment === 'end') ||
      (p.orientation === 'bottom' && p.alignment === 'start')
    );
  }

  // tslint:disable-next-line no-identical-functions
  private alignedBottom(p) {
    return (
      p.orientation === 'bottom' ||
      (p.orientation === 'left' && p.alignment === 'start') ||
      (p.orientation === 'right' && p.alignment === 'end')
    );
  }

  private getViewPort(element) {
    const { left, right, width } = element.getBoundingClientRect();
    const height = window.innerHeight;
    const top = 0;
    return {
      top,
      left,
      right,
      width,
      height,
      x: left,
      y: top,
      bottom: top + height,
    };
  }
}

const arrowSize = 10;
const outerArrowSize = arrowSize / 2;
const borderSize = 2;
const innerArrowSize = outerArrowSize - borderSize;

const opposite = side => {
  return {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  }[side];
};

const mainBorder = side => `
  &.${side} {
    :before {
      border-${side}-color: ${colors.white};
      ${opposite(side)}: -${outerArrowSize * 2}px;
    }
    :after {
      border-${side}-color: ${colors.green100};
      ${opposite(side)}: -${innerArrowSize * 2 - 1}px;
    }
  }
`;

const sideBorder = side => `
  :before {
    border-${side}-color: ${colors.white};
    ${side}: -${borderSize}px;
  }
  :after {
    border-${side}-color: ${colors.green100};
    ${side}: 0;
  }
`;

export default styled(GraphToolTipContent)`
  position: fixed;
  background: ${colors.green100};
  border: ${borderSize}px solid ${colors.white};
  border-radius: 4px;
  color: ${colors.green500};
  min-width: 100px;
  pointer-events: none;

  &.text {
    max-width: 300px;
    padding: 1rem;
    text-align: center;
  }

  &.top.end,
  &.left.start {
    border-bottom-right-radius: 0;
  }

  &.top.start,
  &.right.end {
    border-bottom-left-radius: 0;
  }

  &.bottom.end,
  &.right.start {
    border-top-left-radius: 0;
  }

  &.bottom.start,
  &.left.end {
    border-top-right-radius: 0;
  }

  :before,
  :after {
    content: ' ';
    border: solid transparent;
    width: 0;
    height: 0;
    position: absolute;
  }

  :before {
    border-width: ${outerArrowSize}px;
  }

  :after {
    border-width: ${innerArrowSize}px;
  }

  ${mainBorder('top')};
  ${mainBorder('left')};
  ${mainBorder('bottom')};
  ${mainBorder('right')};

  &.bottom.end,
  &.top.start {
    ${sideBorder('left')};
  }

  &.top.end,
  &.bottom.start {
    ${sideBorder('right')};
  }

  &.left.end,
  &.right.start {
    ${sideBorder('top')};
  }

  &.right.end,
  &.left.start {
    ${sideBorder('bottom')};
  }
`;
